import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SubscriptionService } from '../services/subscription.service';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { CancelDialogComponent } from '../cancelDialog/cancelDialog.component';
import { HistoryEventService } from '../services/history.event.service';
import { CircleProgress } from '../circle-progress/circle-progress';
import { AccountService } from '../services/account';
import { LogService } from '../services/log.service';
import { environment } from '../../environments/environment';
import { delay, map, Observable, of } from 'rxjs';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { CancelBottomSheetComponent } from '../cancelBottomSheet/cancelBottomSheet.component';
import { DeviceDetectorService } from 'ngx-device-detector';

declare function pay(paymentCallback: any, settings: PaymentSettings, accountId: string, userMail: string, payAmount: number, subName: string): any;

const MODULES: any[] = [
  MatButtonModule,
  MatIconModule,
  MatFormFieldModule,
  FormsModule,
  ReactiveFormsModule,
  CommonModule,
  CircleProgress
];

@Component({
  selector: 'app-subscription',
  standalone: true,
  imports: [MODULES],
  templateUrl: './subscription.component.html',
  styleUrl: './subscription.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubscriptionComponent {
  private subscriptionService = inject(SubscriptionService);
  private logService = inject(LogService);

  private changeDetectorRef = inject(ChangeDetectorRef);

  public activeSubscription: AccountSubscription = {} as AccountSubscription;
  public activeSubscriptionPlan: SubscriptionPlan = {} as SubscriptionPlan;
  public isHaveSubscription: boolean = false;
  //Пробная подписка
  public isTrial: boolean = false;
  //У пробной подписки завершился срок действия
  public isTrialCompleted: boolean = false;
  //У платной подписки завершился срок действия
  public isCompleted: boolean = false;
  //Подписка отменена
  public isCancelled: boolean = false;
  //Подписка отклонена
  public isDeclined: boolean = false;
  public isNextSubscriptionDeclined: boolean = false;
  readonly dialog = inject(MatDialog);
  readonly bottomSheet = inject(MatBottomSheet);
  public isDataLoaded: boolean = false;
  public progress: number = 0;
  public cardTypeName: string = '';
  private isMobileDesign: boolean = false;
  public isAgreementChecked: boolean = false;
  public buttonStyle: string = 'gray';

  constructor(
    private historyEventService: HistoryEventService,
    private accountService: AccountService,
    private deviceService: DeviceDetectorService
  ) {
    this.historyEventService.getEvent().subscribe(event => {
      this.logService.LogMessage("Подписки. Получено событие " + event);
      if (event == 'account') {
        this.getActiveSubscription().subscribe();
        this.getActiveSubscriptionPlan();
        this.historyEventService.setEvent('history')
      }
    });

    this.isMobileDesign = this.deviceService.isMobile();
  }

  private setCardType(cardType: number) {
    switch (cardType) {
      case 1: this.cardTypeName = "mir";
        break;
      case 2: this.cardTypeName = "visa";
        break;
      case 3: this.cardTypeName = "maestro";
        break;
      case 4: this.cardTypeName = "mastercard";
        break;
      default: this.cardTypeName = "none"
    }
  }

  /**
   * Получение информации о активной подписке клиента
   */
  getActiveSubscription(): Observable<any> {
    return new Observable(subscriber => {
      this.subscriptionService.getActiveAccountSubscriptions()
        .subscribe(data => {
          this.isDataLoaded = true;
          this.activeSubscription = data;
          this.isHaveSubscription = data != undefined;
          this.isTrial = data != undefined && data.type == 'trial' && data.state == 'active';
          this.isTrialCompleted = data != undefined && data.type == 'trial' && data.state == 'completed';
          this.isCompleted = data != undefined && data.type != 'trial' && data.state == 'completed';
          this.isCancelled = data != undefined && data.state == 'cancelled';
          this.isDeclined = data != undefined && data.state == 'declined';
          this.isNextSubscriptionDeclined = data != undefined && data.nextSubscriptionState == 'declined';
          if (data && data.paymentSystemType) {
            this.setCardType(this.activeSubscription.paymentSystemType);
          }
          if (data && data.remainingDays && data.periodDays) {
            this.progress = Math.ceil(data.remainingDays / data.periodDays * 100);
            if (this.progress > 100) {
              this.progress = 100;
            }
          }
          var stateName = data == undefined ? 'completed' : data.state;
          this.subscriptionService.setSubscriptionStatus(stateName);
          this.historyEventService.setEvent('subscription')
          this.changeDetectorRef.markForCheck();

          subscriber.next([]);
          subscriber.complete();
        });
    })
  }

  getActiveSubscriptionPlan() {
    this.subscriptionService.getActiveSubscriptionPlan()
      .subscribe(data => {
        this.activeSubscriptionPlan = data;
        this.changeDetectorRef.markForCheck();
      });
  }

  openDialog(): void {
    if (this.isMobileDesign) {
      const dialogRef = this.bottomSheet.open(CancelBottomSheetComponent, { data: { mode: 'confirm' } });
      dialogRef.afterDismissed().subscribe(result => {
        if (result !== undefined && result == 'yes') {
          this.subscriptionService.cancelSubscription().subscribe(result => {
            this.getActiveSubscription().subscribe(() =>
              this.bottomSheet.open(CancelBottomSheetComponent, { data: { mode: 'completed' } })
            );
          });
        }
      })
    }
    else {
      const dialogRef = this.dialog.open(CancelDialogComponent, {
        height: '280px',
        width: '520px',
        data: { mode: 'confirm' }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result !== undefined && result == 'yes') {
          this.subscriptionService.cancelSubscription().subscribe(result => {
            this.getActiveSubscription().subscribe(() =>
              this.dialog.open(CancelDialogComponent, {
                height: '280px',
                width: '520px',
                data: { mode: 'completed' }
              })
            );
          });
        }
      });
    }
  }

  public createPayment() {
    if (!this.isAgreementChecked) {
      return;
    }
    this.logService.LogMessage("Начало оплаты");

    var isSetDate = false;
    var endDate = new Date();
    if (this.activeSubscription && this.isTrial == false) {
      endDate = new Date(this.activeSubscription.endDate);
      if (endDate > new Date()) {
        endDate.setDate(endDate.getDate() + this.activeSubscriptionPlan.period);
        isSetDate = true;
      }
    }

    var paySum = this.activeSubscriptionPlan.totalCost;
    var accountInfo = this.accountService.getAccountInfo();
    let settings: PaymentSettings = {
      terminalId: environment.terminalId,
      period: this.activeSubscriptionPlan.period,
      startDate: endDate,
      isSetStartDate: isSetDate,
    };
    var subName = 'Подписка ' + this.activeSubscriptionPlan.name;
    pay(this.paymentEvent, settings, accountInfo.id.toString(), accountInfo.accountEmail, paySum, subName);
  }

  paymentEvent = (event: any) => {
    if ("success" in event) {
      if (event.success) {
        this.logService.LogMessage("Оплата прошла успешно");
        of('').pipe(
          delay(3500)
        )
          .subscribe(() => {
            this.logService.LogMessage("Обновление информации о подписке");
            this.getActiveSubscription().subscribe(() =>
              this.historyEventService.setEvent('history')
            );
          })
      }
      else {
        this.logService.LogMessage("Оплата не выполнена: " + event.message + " Код ошибки: " + event.code);
      }
    }
  }

  public getRemaingDaysText(remDays: number) {
    var daysText = "дней";
    var days = remDays;
    while (days > 20) {
      days -= 20;
    };
    if (remDays > 30) {
      days -= 10;
    }
    switch (days) {
      case 1:
        daysText = "день"
        break;
      case 2:
      case 3:
      case 4:
        daysText = "дня"
        break;
    }
    return daysText;
  }

  public onAgreementChange() {
    this.buttonStyle = this.isAgreementChecked ? "black" : "gray";
  }
}
